import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  DataTable,
  DataTableColumn,
  DateRange,
  DateRangeInput,
  Icon,
  Input,
  Loader,
  PaginationPageSizes,
  Select,
  useDebounce
} from "best-common-react";
import { searchEmailLists } from "../api/AuthenticatedClient.ts";
import { EmailListRow } from "../types/emailList.ts";
import { useDropdowns } from "../contexts/DropdownsContext.tsx";
import { CreatedAtFormatter, TeamFormatter } from "../components/TableFormatters.tsx";
import { UploadedByOptions } from "../constants/common.ts";
import EmailListUploadSummaryModal from "../components/EmailListUploadSummaryModal.tsx";

const Container = styled.div`
  height: 100%;
  width: 100%;
  padding: 1rem;
`;

const HeaderContainer = styled.div`
  padding-bottom: 1rem;
  border-bottom: 1px solid #000;
`;

const TitleContainer = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const TableContainer = styled.div`
  margin-top: 1rem;
`;

const FilterInput = styled.div`
  width: 250px;
`;

const StatusOptions = [
  { value: "All", label: "All" },
  { value: "In Queue", label: "In Queue" },
  { value: "Processing List", label: "Processing List" },
  { value: "Processing Opt-Ins", label: "Processing Opt-Ins" },
  { value: "List Ready in Bullseye", label: "List Ready in Bullseye" },
  { value: "Opt-Ins Processed", label: "Opt-Ins Processed" },
  { value: "List Ready for ESP", label: "List Ready for ESP" },
  { value: "Sending to ESP", label: "Sending to ESP" },
  { value: "Sent to ESP", label: "Sent to ESP" },
];

type Filters = {
  search: string;
  teamId?: number;
  status?: string;
  uploadedBy?: string;
  createdFrom?: Date;
  createdTo?: Date;
  timeZone: string;
};

const BrowseEmailLists: React.FC = () => {
  const { teamOptions } = useDropdowns();
  const [search, setSearch] = useState<string>("");
  const [emailLists, setEmailLists] = useState<EmailListRow[]>([]);
  const [filters, setFilters] = useState<Filters>({
    search: "",
    status: "All",
    uploadedBy: "Me",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const [totalItems, setTotalItems] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState<PaginationPageSizes>(25);
  const [loading, setLoading] = useState(false);
  const [selectedEmailListRow, setSelectedEmailListRow] = useState<EmailListRow>();
  const [showEmailListUploadSummaryModal, setShowEmailListUploadSummaryModal] = useState(false);

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters({ ...filters, [key]: value });
    setPageNumber(0);
  };

  const onSearchTermChange = useDebounce(async (value: string) => {
    setFilters({ ...filters, search: value });
  }, 400);

  const onSearchEmailLists = () => {
    setLoading(true);
    searchEmailLists(filters, pageNumber, pageSize)
      .then(res => {
        setEmailLists(res.content);
        setTotalItems(res.page.totalElements);
        if (!!selectedEmailListRow) {
          setSelectedEmailListRow(res.content.find(row => row.id === selectedEmailListRow.id));
        }
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    onSearchEmailLists();
  }, [filters, pageNumber, pageSize]);

  const Columns: DataTableColumn<EmailListRow>[] = [
    {
      name: "Team",
      key: "",
      width: 50,
      readonlyFormatter: TeamFormatter
    },
    {
      name: "Name",
      key: "name",
      width: 250
    },
    {
      name: "Queue Position",
      key: "queuePosition",
      width: 125
    },
    {
      name: "Segment ID",
      key: "id",
      width: 100
    },
    {
      name: "Status",
      key: "status",
      width: 200
    },
    {
      name: "Type",
      key: "uploadType",
      width: 100
    },
    {
      name: "Source",
      key: "sourceCode",
      width: 75
    },
    {
      name: "Rows",
      key: "rows",
      width: 75
    },
    {
      name: "Cols",
      key: "cols",
      width: 75
    },
    {
      name: "Headers",
      key: "headers",
      width: 250
    },
    {
      name: "Fields",
      key: "fields",
      width: 250
    },
    {
      name: "Created By",
      key: "createdBy",
      width: 150
    },
    {
      name: "Created At",
      key: "createdAt",
      width: 250,
      readonlyFormatter: CreatedAtFormatter
    }
  ];

  const onRowClock = (_: number, value: EmailListRow) => {
    setSelectedEmailListRow(value);
    setShowEmailListUploadSummaryModal(true);
  };

  return (
    <>
      <EmailListUploadSummaryModal
        show={showEmailListUploadSummaryModal}
        emailListRow={selectedEmailListRow}
        refresh={onSearchEmailLists}
        onClose={() => setShowEmailListUploadSummaryModal(false)}
      />
      <Container>
        <HeaderContainer>
          <TitleContainer>
            <span>Browse Email Lists</span>
            {loading ? <Loader type="spinner" size="20" /> : <Icon iconName="fa-refresh" onClick={onSearchEmailLists} />}
          </TitleContainer>

          <FiltersContainer>
            <FilterInput>
              <Input
                id="search"
                label="Search"
                value={search}
                onChange={value => {
                  setSearch(value);
                  onSearchTermChange(value);
                }}
              />
            </FilterInput>

            <FilterInput>
              <Select
                id="club-select"
                label="Club"
                value={teamOptions.find(option => option.value.id === filters.teamId)}
                options={teamOptions}
                clearable
                onChange={option => {
                  handleFilterChange("teamId", option?.value.id);
                }}
              />
            </FilterInput>

            <FilterInput>
              <Select
                id="status-select"
                label="Status"
                value={StatusOptions.find(option => option.value === filters.status)}
                options={StatusOptions}
                onChange={option => {
                  handleFilterChange("status", option?.value);
                }}
              />
            </FilterInput>

            <FilterInput>
              <Select
                id="uploaded-by-select"
                label="Uploaded By"
                value={UploadedByOptions.find(option => option.value === filters.uploadedBy)}
                options={UploadedByOptions}
                onChange={option => {
                  handleFilterChange("uploadedBy", option?.value);
                }}
              />
            </FilterInput>

            <div>
              <DateRangeInput
                id="birth-date-select"
                label="Created"
                value={{
                  start: filters.createdFrom,
                  end: filters.createdTo
                }}
                onChange={(value: DateRange<Date> | "N/A") => {
                  const { start, end } = value as DateRange<Date>;
                  setFilters({ ...filters, createdFrom: start, createdTo: end });
                }}
                clearable
                invalidDateMessage=""
              />
            </div>
          </FiltersContainer>
        </HeaderContainer>

        <TableContainer>
          <DataTable
            data={emailLists}
            columns={Columns}
            tableHeights={{
              autogrow: true,
              maxHeightOffset: 400
            }}
            rowClick={{
              onClick: onRowClock,
              isClickable: () => true
            }}
            pagination={{
              totalItems,
              pageNumber,
              setPageNumber,
              pageSize,
              setPageSize
            }}
          />
        </TableContainer>
      </Container>
    </>
  );
};

export default BrowseEmailLists;
